/**
 * Every Citizen Web page the platform sends a citizen to — from a push, a bell
 * notification, an inbox message or an email. One list, held three ways:
 *
 *   • the API types the links it writes with it (`CitizenActionUrl`), so a page
 *     that is not here cannot be linked;
 *   • Citizen Web routes every one (journey-web `citizen-portal-paths.spec.ts`);
 *   • the phone opens every one in the app, or on the web where the page lives
 *     only there (citizen-mobile `action-url.ts`, a `Record` over this list).
 *
 * Until 21 Oct 2026 the phone pushed any `/…` as its own route: `/dashboard`
 * (eight invitation and sponsorship messages), `/journeys/manage` and `/testit`
 * landed on its "Not Found" screen.
 */
export const CITIZEN_PORTAL_PATHS = [
  '/dashboard',
  '/checkup',
  '/teleportal',
  '/qultureup',
  '/qultureup/academy',
  '/care',
  '/passport',
  '/inout',
  '/inout/agency',
  '/inout/notices',
  '/billing',
  '/settings',
  '/testit',
  '/journeys/manage',
  '/accept-invite',
  '/sponsorship/offer',
] as const;

export type CitizenPortalPath = (typeof CITIZEN_PORTAL_PATHS)[number];

/** One of those pages, with or without its own query (`/settings?tab=security`). */
export type CitizenPortalHref = CitizenPortalPath | `${CitizenPortalPath}?${string}`;

/** An address on a portal, written whole (an email's link, a business page). */
export type AbsoluteUrl = `https://${string}` | `http://${string}`;

/** Where a notification sends its reader: a Citizen Web page, or an address written whole. */
export type CitizenActionUrl = CitizenPortalHref | AbsoluteUrl;

const PATHS: ReadonlySet<string> = new Set(CITIZEN_PORTAL_PATHS);

/** Whether a path (no query, no fragment) is one of the pages above. */
export function isCitizenPortalPath(path: string): path is CitizenPortalPath {
  return PATHS.has(path);
}
